import { useCallback, useEffect, useRef, useState } from 'react';

import { Space, Text } from '@mantine/core';
import { IconBarcodeOff } from '@tabler/icons-react';
import { readBarcode } from './read-barcode';
import styles from './styles.module.css';

export interface DecodedSymbol {
	typeName: string;
	data: string;
}

let offscreenCanvas: OffscreenCanvas | undefined;

type ScannerProps = {
	onScan: (code: string) => void;
	ignoreCodes?: Set<string>;
	width?: number | string;
	height?: number | string;
	/** Pause scanning while keeping the camera active */
	paused?: boolean;
	/** Custom detection interval in milliseconds (default: ~83ms for 12fps) */
	detectionInterval?: number;
	/** Custom wrapper height override (e.g. passing in 160 will make the wrapper height "calc(100vh - 160px)")
	 *  default is 80px.
	 */
	wrapperHeightPadding?: number;
	roundedCorners?: boolean;
};

export const Scanner = ({
	onScan,
	ignoreCodes,
	width,
	height,
	paused = false,
	detectionInterval = 1000 / 12, // Default to ~12fps
	wrapperHeightPadding = 80,
	roundedCorners,
}: ScannerProps) => {
	const videoRef = useRef<HTMLVideoElement>(null);
	const codeCooldown = useRef<string | null>(null);

	// Handler device not found
	const [deviceNotFound, setDeviceNotFound] = useState(false);

	// Kick off the camera
	useEffect(() => {
		(async () => {
			try {
				const mediaStream = await navigator.mediaDevices.getUserMedia({
					audio: false,
					video: { facingMode: 'environment' },
				});

				if (!videoRef.current) throw new Error('Video ref is not set.');
				videoRef.current.srcObject = mediaStream;
			} catch {
				setDeviceNotFound(true);
			}
		})();
	}, []);

	const handleCodeCooldown = useCallback((code: string) => {
		codeCooldown.current = code;
		setTimeout(() => {
			codeCooldown.current = null;
		}, 3000);
	}, []);

	// Kick off detection
	useEffect(() => {
		let animationHandle: number;
		let isProcessing = false;

		// Use configurable detection interval for performance tuning
		const DETECTION_INTERVAL = detectionInterval;
		let lastDetectionTime = 0;

		// Use smaller resolution for faster processing
		const MAX_PROCESSING_WIDTH = 1280;
		const MAX_PROCESSING_HEIGHT = 720;

		const detect = async () => {
			// Skip detection if component is paused
			if (paused) {
				animationHandle = requestAnimationFrame(detect);
				return;
			}

			const now = performance.now();
			const timeSinceLastDetection = now - lastDetectionTime;

			// Skip this frame if we're still processing or haven't waited long enough
			if (isProcessing || timeSinceLastDetection < DETECTION_INTERVAL) {
				animationHandle = requestAnimationFrame(detect);
				return;
			}

			isProcessing = true;
			lastDetectionTime = now;

			try {
				const video = videoRef.current;
				if (!video || !video.videoHeight || !video.videoWidth) {
					animationHandle = requestAnimationFrame(detect);
					return;
				}

				// Calculate optimal processing dimensions (maintain aspect ratio)
				const aspectRatio = video.videoWidth / video.videoHeight;
				let processingWidth = Math.min(video.videoWidth, MAX_PROCESSING_WIDTH);
				let processingHeight = Math.min(video.videoHeight, MAX_PROCESSING_HEIGHT);

				if (processingWidth / processingHeight > aspectRatio) {
					processingWidth = processingHeight * aspectRatio;
				} else {
					processingHeight = processingWidth / aspectRatio;
				}

				// Create or reuse canvas with optimal size
				if (
					!offscreenCanvas ||
					offscreenCanvas.width !== processingWidth ||
					offscreenCanvas.height !== processingHeight
				) {
					offscreenCanvas = new OffscreenCanvas(processingWidth, processingHeight);
				}

				const context = offscreenCanvas?.getContext('2d', { willReadFrequently: true });
				if (!context) throw new Error('Could not get 2d context from offscreen canvas.');

				// Draw scaled-down image for faster processing
				context.drawImage(video, 0, 0, processingWidth, processingHeight);

				// Process only center region (where QR codes are typically scanned)
				const roiPadding = 0.1; // 10% padding around center
				const roiX = Math.floor(processingWidth * roiPadding);
				const roiY = Math.floor(processingHeight * roiPadding);
				const roiWidth = Math.floor(processingWidth * (1 - 2 * roiPadding));
				const roiHeight = Math.floor(processingHeight * (1 - 2 * roiPadding));

				const imageData = context.getImageData(roiX, roiY, roiWidth, roiHeight);

				// Detect barcodes using optimized main thread processing
				const symbols = await readBarcode(imageData);

				for (let i = 0; i < symbols.length; i++) {
					const symbol = symbols[i];

					if (!ignoreCodes?.has(symbol.data) && codeCooldown.current !== symbol.data) {
						onScan(symbol.data);
						handleCodeCooldown(symbol.data);
					}
				}
			} catch {
				// Silently handle errors to prevent console noise
				// Error during barcode detection - this is expected occasionally
			} finally {
				isProcessing = false;
			}

			animationHandle = requestAnimationFrame(detect);
		};

		// Start detection with a small delay to ensure video is ready
		const timeoutHandle = window.setTimeout(() => {
			animationHandle = requestAnimationFrame(detect);
		}, 100);

		return () => {
			cancelAnimationFrame(animationHandle);
			clearTimeout(timeoutHandle);
		};
	}, [videoRef, onScan, ignoreCodes, paused, detectionInterval]);

	return (
		<div
			className={styles.scannerWrapper}
			style={{ height: `calc(100vh - ${wrapperHeightPadding}px)` }}
		>
			{/* This is a load bearing div. Please do not remove it, as it will break in iOS Safari
			    if this is not here. */}
			<div
				style={{
					width: width || '100%',
					height: height || '100%',
					borderRadius: roundedCorners ? '10px' : undefined,
				}}
				className={styles.container}
			>
				<video
					ref={videoRef}
					muted
					playsInline
					disablePictureInPicture
					autoPlay
					className={styles.video}
					width="100%"
					height="100%"
				/>
				{!deviceNotFound && <ScanCrosshair />}
				{deviceNotFound && (
					<div
						style={{
							height: '100vh',
							width: '100%',
							position: 'absolute',
							top: 0,
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							color: 'white',
							fontSize: 24,
							fontWeight: 600,
							textAlign: 'center',
							gap: 6,
						}}
					>
						<IconBarcodeOff strokeWidth={1.2} opacity={0.4} size={100} />
						<Space h="md" />
						No camera found
						<Text size="sm" opacity={0.4}>
							Please check your device settings and try again.
						</Text>
					</div>
				)}
			</div>
		</div>
	);
};

const ScanCrosshair = () => {
	return (
		<div className={styles.crosshairContainer}>
			<div className={styles.crosshairBox}>
				<div className={styles.crosshairCornerTopRight} />
				<div className={styles.crosshairCornerBottomRight} />
				<div className={styles.crosshairCornerBottomLeft} />
				<div className={styles.crosshairCornerTopLeft} />
			</div>
		</div>
	);
};
